import { resolveId } from '@medplum/core';
import type { ServiceRequest } from '@medplum/fhirtypes';

/** Identifier system Health Gorilla stamps on requisition-related resources (SR, DocRef). */
export const HEALTH_GORILLA_REQUISITION_SYSTEM = 'https://www.healthgorilla.com';

/**
 * Extract the Health Gorilla requisition identifier from a `ServiceRequest`.
 *
 * Looks at `ServiceRequest.requisition` first (where HG-emitted SRs put the
 * canonical requisition id), then falls back to scanning `ServiceRequest.identifier[]`
 * for any identifier with the Health Gorilla system. Returns `undefined` for
 * SRs that aren't HG-bound.
 *
 * Used by the lab-order detail view to look up the corresponding
 * `LabOrderRequisition` and `SpecimenLabel` `DocumentReference` PDFs, both of
 * which are linked via this identifier rather than a `based-on` reference.
 */
export function getHealthGorillaRequisitionId(serviceRequest: ServiceRequest): string | undefined {
  if (serviceRequest.requisition?.system === HEALTH_GORILLA_REQUISITION_SYSTEM) {
    return serviceRequest.requisition.value;
  }
  const identifier = serviceRequest.identifier?.find((id) => id.system === HEALTH_GORILLA_REQUISITION_SYSTEM);
  return identifier?.value;
}

/** A QR reference + the SR that holds it, used to label each AOE group with its source test. */
export type AoeReference = { sourceServiceRequest: ServiceRequest; questionnaireResponseId: string };

/**
 * Walk a lab-order family and return one `AoeReference` per `QuestionnaireResponse/...` reference
 * found across `parent.supportingInfo[]` and each child's `supportingInfo[]`. Dedupes by QR id
 * (a single QR could in theory be referenced from multiple SRs in the family).
 *
 * Health Gorilla attaches AOE QRs per-test on child SRs (sub-orders) — `parent.supportingInfo[]`
 * for HG-emitted parents typically holds `RequestGroup` + `LabOrderRequisition` + `SpecimenLabel`
 * refs, not QRs — but we walk both to handle non-HG sources or future write-paths that put the
 * QR ref directly on the parent.
 *
 * NOTE: callers that surface the user-visible "an order" concept should pass the parent
 * (HG-profile order) here. The lab-orders list view filters by `_profile=MedplumHealthGorillaOrder`
 * so users only ever click into parents. If a future entry point surfaces a child SR directly,
 * this walk would miss QRs on the parent and on sibling children — fix when that happens.
 */
export function collectAoeReferences(parent: ServiceRequest, children: ServiceRequest[]): AoeReference[] {
  const seen = new Set<string>();
  const result: AoeReference[] = [];
  const sources: ServiceRequest[] = [parent, ...children];
  for (const sr of sources) {
    for (const ref of sr.supportingInfo ?? []) {
      if (!ref.reference?.startsWith('QuestionnaireResponse/')) continue;
      const qrId = resolveId(ref);
      if (!qrId || seen.has(qrId)) continue;
      seen.add(qrId);
      result.push({ sourceServiceRequest: sr, questionnaireResponseId: qrId });
    }
  }
  return result;
}

/** Pull a display-friendly test name off an SR holding an AOE QR. Used as a sub-heading. */
export function getServiceRequestTestName(sr: ServiceRequest): string | undefined {
  const coding = sr.code?.coding?.[0];
  return sr.code?.text ?? coding?.display ?? coding?.code;
}
